import React, {useState, useEffect, useRef, useCallback} from "react"
import {
    Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar,
    TextField, InputAdornment, Chip, Divider, CircularProgress, Button,
    Paper, IconButton, Tabs, Tab, Badge, Tooltip
} from "@mui/material"
import {
    Search as SearchIcon, Mail as MailIcon, AccessTime as AccessTimeIcon,
    MoreHoriz as MoreHorizIcon, CheckCircle as CheckCircleIcon,
    Send as SendIcon, AttachFile as AttachFileIcon,
    EmojiEmotions as EmojiIcon, Done as DoneIcon, DoneAll as DoneAllIcon,
    VolumeUp as VolumeUpIcon, Close as CloseIcon,
    ArrowBack as ArrowBackIcon
} from "@mui/icons-material"
import {Client} from "@stomp/stompjs"
import {formatDistanceToNow} from "date-fns"
import {hy, ru} from "date-fns/locale"
import Cookies from "js-cookie"
import axiosInstance from "../../config/axios-config"
import i18n from "../../i18n/i18n"
import {useTranslation} from "react-i18next"
import {config} from "../../config/environment"
import {PageContainer} from "../common/page-container"
import GlowingMessage from "../../errors/glowing-message"
import {eventBus} from "../../event-bus"

const AllMessages = ({navOpen}) => {
    const {t} = useTranslation()
    const tenantId = Cookies.get("currentTenantId")

    // ─── Conversations state ───────────────────────────────────────────────────
    const [conversations, setConversations] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [unreadCount, setUnreadCount] = useState(0)
    const [page, setPage] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    const [loadingConversations, setLoadingConversations] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [searchQuery, setSearchQuery] = useState("")

    // ─── Active chat state ────────────────────────────────────────────────────
    const [selectedConversation, setSelectedConversation] = useState(null)
    const [messages, setMessages] = useState([])
    const [newText, setNewText] = useState("")
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [hasMoreMessages, setHasMoreMessages] = useState(true)
    const [messagePage, setMessagePage] = useState(0)

    // ─── WebSocket refs ───────────────────────────────────────────────────────
    const convStompRef = useRef(null)
    const chatStompRef = useRef(null)
    const messagesEndRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const notificationSoundRef = useRef(null)
    const [soundEnabled, setSoundEnabled] = useState(true)
    const loadingMessagesRef = useRef(false)

    // ─── Error/feedback ───────────────────────────────────────────────────────
    const [errorMsg, setErrorMsg] = useState("")

    // ─── Helpers ──────────────────────────────────────────────────────────────
    const getLocale = () => {
        switch (i18n.language) {
            case "ru":
                return ru
            case "hy":
                return hy
            default:
                return undefined
        }
    }

    const formatDate = useCallback((dateString) => {
        try {
            return formatDistanceToNow(new Date(dateString), {addSuffix: true, locale: getLocale()})
        } catch {
            return ""
        }
    }, [])

    const formatTime = (timestamp) => {
        try {
            return new Date(timestamp).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})
        } catch {
            return ""
        }
    }

    const formatDateLabel = (timestamp) => {
        const msgDate = new Date(timestamp)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        ;[msgDate, today, yesterday].forEach(d => d.setHours(0, 0, 0, 0))
        if (msgDate.getTime() === today.getTime()) return "Today"
        if (msgDate.getTime() === yesterday.getTime()) return "Yesterday"
        return msgDate.toLocaleDateString("en-US", {month: "2-digit", day: "2-digit", year: "numeric"})
    }

    const isDifferentDay = (ts1, ts2) => {
        const d1 = new Date(ts1), d2 = new Date(ts2)
        return d1.getDate() !== d2.getDate() ||
            d1.getMonth() !== d2.getMonth() ||
            d1.getFullYear() !== d2.getFullYear()
    }

    const addressToString = (address) => {
        if (!address) return ""
        return `${address.street || ""}, ${address.city || ""}, ${address.country || ""}`.trim()
    }

    const playSound = useCallback(() => {
        if (!soundEnabled || !notificationSoundRef.current) return
        notificationSoundRef.current.currentTime = 0
        notificationSoundRef.current.volume = 0.5
        notificationSoundRef.current.play().catch(() => {
        })
    }, [soundEnabled])

    // ─── Fetch unread count ───────────────────────────────────────────────────
    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await axiosInstance.get("/api/v1/conversations/unread-count", {
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}
            })
            setUnreadCount(res.data)
        } catch (e) {
            console.error("Error fetching unread count:", e)
        }
    }, [])

    // ─── Fetch conversations ──────────────────────────────────────────────────
    const fetchConversations = useCallback(async (pageNum = 0, append = false) => {
        if (!tenantId) return
        pageNum === 0 ? setLoadingConversations(true) : setLoadingMore(true)
        try {
            const res = await axiosInstance.get("/api/v1/conversations", {
                params: {page: pageNum, size: 20},
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}
            })
            const {content, last, totalElements} = res.data
            setTotalCount(totalElements)
            setConversations(prev => append ? [...prev, ...content] : content)
            setHasMore(!last)
            setPage(pageNum)
        } catch (e) {
            setErrorMsg(e.response?.data?.message || "Failed to fetch conversations")
        } finally {
            setLoadingConversations(false)
            setLoadingMore(false)
        }
    }, [tenantId])

    // ─── Mark conversation as read ────────────────────────────────────────────
    const markAsRead = useCallback(async (conversationId) => {
        try {
            await axiosInstance.put(`/api/v1/chat-messages/${conversationId}/mark-as-read`, {}, {
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}
            })
            setConversations(previous => previous.map(conversation =>
                conversation.conversationId === conversationId
                    ? {...conversation, unreadCount: 0, lastMessage: {...conversation.lastMessage, isRead: true}}
                    : conversation
            ))
            eventBus.emit("messageReadStateChanged", {tenantId, conversationId})
            fetchUnreadCount()
        } catch (e) {
            console.error("Error marking as read:", e)
            setErrorMsg(e.response?.data?.message || "Failed to mark conversation as read")
        }
    }, [fetchUnreadCount, tenantId])

    // ─── Mark all as read ─────────────────────────────────────────────────────
    const markAllAsRead = useCallback(async () => {
        try {
            await axiosInstance.put("/api/v1/messages/read-all", {}, {
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}
            })
            eventBus.emit("messageReadStateChanged", {tenantId, all: true})
            fetchUnreadCount()
            fetchConversations(0, false)
        } catch (e) {
            setErrorMsg(e.response?.data?.message || "Failed to mark all as read")
        }
    }, [fetchUnreadCount, fetchConversations, tenantId])

    // ─── Fetch messages for active conversation ───────────────────────────────
    const fetchMessages = useCallback(async (conversationId, pageNum = 0, prepend = false) => {
        if (loadingMessagesRef.current) return
        loadingMessagesRef.current = true
        setLoadingMessages(true)
        try {
            const res = await axiosInstance.get(`/api/v1/chat-messages/${conversationId}`, {
                params: {page: pageNum, size: 50}
            })
            const data = res.data.content
            if (data.length < 50) setHasMoreMessages(false)

            if (prepend) {
                const heightBefore = scrollContainerRef.current?.scrollHeight || 0
                setMessages(prev => [...data, ...prev])
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop =
                            scrollContainerRef.current.scrollHeight - heightBefore
                    }
                }, 100)
            } else {
                setMessages(data)
                setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior: "smooth"}), 100)
            }
        } finally {
            loadingMessagesRef.current = false
            setLoadingMessages(false)
        }
    }, [])

    // ─── Select a conversation ────────────────────────────────────────────────
    const handleSelectConversation = useCallback(async (conversation) => {
        setSelectedConversation(conversation)
        setMessages([])
        setMessagePage(0)
        setHasMoreMessages(true)

        if (conversation.unreadCount > 0 ||
            (!conversation.lastMessage?.isRead && conversation.lastMessage?.recipientId === tenantId)) {
            markAsRead(conversation.conversationId)
        }

        await fetchMessages(conversation.conversationId, 0, false)
    }, [tenantId, markAsRead, fetchMessages])

    // ─── Handle scroll to top → load older messages ──────────────────────────
    const handleScroll = () => {
        const container = scrollContainerRef.current
        if (!container || loadingMessagesRef.current || !hasMoreMessages || !selectedConversation) return
        if (container.scrollTop === 0) {
            const nextPage = messagePage + 1
            setMessagePage(nextPage)
            fetchMessages(selectedConversation.conversationId, nextPage, true)
        }
    }

    // ─── Send a message ───────────────────────────────────────────────────────
    const sendMessage = () => {
        if (!newText.trim() || !chatStompRef.current || !selectedConversation) return
        const recipientId = selectedConversation.participantOneId === tenantId
            ? selectedConversation.participantTwoId
            : selectedConversation.participantOneId
        chatStompRef.current.publish({
            destination: `/app/queue/conversations/${selectedConversation.conversationId}`,
            body: JSON.stringify({
                text: newText.trim(),
                senderId: tenantId,
                createdAt: new Date().toISOString(),
                conversationId: selectedConversation.conversationId,
                recipientId,
                messageType: "TEXT",
                isRead: false
            })
        })
        setNewText("")
    }

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    // ─── Conversation list WebSocket ──────────────────────────────────────────
    useEffect(() => {
        if (!tenantId) return
        fetchConversations(0, false)
        fetchUnreadCount()

        const client = new Client({
            brokerURL: `${config.WEBSOCKET_URL}/ws`,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                client.subscribe(`/queue/messages/${tenantId}`, () => {
                    playSound()
                    fetchConversations(0, false)
                    fetchUnreadCount()
                })
            },
            onStompError: (f) => console.error("STOMP Error:", f),
            onWebSocketError: (e) => console.error("WS Error:", e),
        })
        client.activate()
        convStompRef.current = client
        return () => {
            client.deactivate()
        }
    }, [tenantId])

    // ─── Chat WebSocket (active conversation) ─────────────────────────────────
    useEffect(() => {
        if (!selectedConversation?.conversationId) return

        const client = new Client({
            brokerURL: `${config.WEBSOCKET_URL}/ws`,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                client.subscribe(
                    `/queue/conversations/${selectedConversation.conversationId}/user/${tenantId}`,
                    (msg) => {
                        const received = JSON.parse(msg.body)
                        setMessages(prev => [...prev, received])
                        setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior: "smooth"}), 50)
                    }
                )
                client.subscribe(
                    `/queue/conversations/${selectedConversation.conversationId}/read-status`,
                    (msg) => {
                        const update = JSON.parse(msg.body)
                        setMessages(prev => prev.map(m =>
                            m.messageId?.toString() === update.messageId?.toString() ? update : m
                        ))
                    }
                )
            },
            onStompError: (f) => console.error("Chat STOMP Error:", f),
            onWebSocketError: (e) => console.error("Chat WS Error:", e),
        })
        client.activate()
        chatStompRef.current = client
        return () => {
            client.deactivate()
        }
    }, [selectedConversation?.conversationId])


    // ─── Filtered conversations ───────────────────────────────────────────────
    const filteredConversations = conversations.filter(conv => {
        const matchesTab =
            activeTab === 0 ? true :
                activeTab === 1 ? (!conv.lastMessage?.isRead && conv.lastMessage?.recipientId === tenantId) :
                    (conv.lastMessage?.isRead || conv.lastMessage?.senderId === tenantId)

        const q = searchQuery.trim().toLowerCase()
        const matchesSearch = !q ||
            conv.sender?.tenantNameEn?.toLowerCase().includes(q) ||
            conv.sender?.tenantNameHy?.toLowerCase().includes(q) ||
            conv.sender?.tenantNameRu?.toLowerCase().includes(q) ||
            conv.lastMessage?.text?.toLowerCase().includes(q)

        return matchesTab && matchesSearch
    })

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PageContainer navOpen={navOpen}>
            <audio ref={notificationSoundRef} preload="auto" style={{display: "none"}}>
                <source src="/notification.mp3" type="audio/mpeg"/>
            </audio>
            <GlowingMessage message={errorMsg} onClose={() => setErrorMsg("")}/>

            {/* Page header */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3}}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {t("Messages")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {totalCount} {t("conversations") || "conversations"} · {unreadCount} {t("unread")}
                    </Typography>
                </Box>
                <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                    <Tooltip title={soundEnabled ? t("SoundOn") : t("SoundOff")}>
                        <IconButton
                            onClick={() => setSoundEnabled(p => !p)}
                            sx={{color: soundEnabled ? "primary.main" : "text.disabled"}}
                        >
                            <VolumeUpIcon/>
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<CheckCircleIcon/>}
                        onClick={markAllAsRead}
                        disabled={unreadCount === 0}
                    >
                        {t("MarkAllAsRead")}
                    </Button>
                </Box>
            </Box>

            {/* Two-column layout */}
            <Box sx={{display: "flex", gap: 2, height: "calc(100vh - 220px)", minHeight: 500}}>

                {/* ── Left panel: conversation list ── */}
                <Paper
                    elevation={2}
                    sx={{
                        width: {xs: "100%", md: 340},
                        display: {
                            xs: selectedConversation ? "none" : "flex",
                            md: "flex"
                        },
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden",
                        flexShrink: 0,
                    }}
                >
                    {/* Search header */}
                    <Box sx={{p: 2, background: "linear-gradient(to right, #f5f7fa, #e4e8f0)", flexShrink: 0}}>
                        <TextField
                            fullWidth size="small"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder={t("SearchChats") || "Search chats..."}
                            variant="outlined"
                            sx={{"& .MuiOutlinedInput-root": {borderRadius: 2, bgcolor: "rgba(255,255,255,0.8)"}}}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{color: "text.secondary"}}/>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Tabs */}
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="fullWidth"
                        sx={{
                            borderBottom: 1, borderColor: "divider", flexShrink: 0,
                            "& .MuiTab-root": {minHeight: 44, textTransform: "none", fontWeight: 500}
                        }}
                    >
                        <Tab label={
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                                {t("All")}
                                <Chip label={totalCount} size="small" variant="outlined"
                                      sx={{height: 18, fontSize: "0.65rem"}}/>
                            </Box>
                        }/>
                        <Tab label={
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                                {t("unread")}
                                {unreadCount > 0 && (
                                    <Chip label={unreadCount} size="small" color="error"
                                          sx={{height: 18, fontSize: "0.65rem"}}/>
                                )}
                            </Box>
                        }/>
                        <Tab label={
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                                {t("read")}
                                <Chip label={totalCount - unreadCount} size="small" variant="outlined"
                                      sx={{height: 18, fontSize: "0.65rem"}}/>
                            </Box>
                        }/>
                    </Tabs>

                    {/* List */}
                    <Box sx={{
                        flex: 1, overflowY: "auto",
                        "&::-webkit-scrollbar": {width: "4px"},
                        "&::-webkit-scrollbar-thumb": {background: "rgba(0,0,0,0.2)", borderRadius: "2px"}
                    }}>
                        {loadingConversations ? (
                            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", p: 6}}>
                                <CircularProgress size={32}/>
                            </Box>
                        ) : filteredConversations.length === 0 ? (
                            <Box sx={{p: 4, textAlign: "center"}}>
                                <MailIcon sx={{fontSize: 48, color: "grey.300", mb: 1}}/>
                                <Typography color="text.secondary" variant="body2">
                                    {activeTab === 1 ? t("UnreadMessagesDesc") : t("AllMessagesDesc")}
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{p: 0}}>
                                {filteredConversations.map((conv, idx) => {
                                    const isUnread = !conv.lastMessage?.isRead && conv.lastMessage?.recipientId === tenantId
                                    const isSelected = selectedConversation?.conversationId === conv.conversationId
                                    return (
                                        <React.Fragment key={conv.conversationId || idx}>
                                            <ListItem
                                                alignItems="flex-start"
                                                onClick={() => handleSelectConversation(conv)}
                                                sx={{
                                                    px: 2, py: 1.5, cursor: "pointer",
                                                    bgcolor: isSelected ? "primary.50" : isUnread ? "action.hover" : "transparent",
                                                    borderLeft: "3px solid",
                                                    borderLeftColor: isSelected ? "primary.main" : "transparent",
                                                    "&:hover": {bgcolor: "action.selected"},
                                                    transition: "all 0.15s ease",
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Badge
                                                        badgeContent={conv.unreadCount || 0}
                                                        color="error"
                                                        overlap="circular"
                                                        sx={{
                                                            "& .MuiBadge-badge": {
                                                                fontSize: "0.6rem",
                                                                height: 16,
                                                                minWidth: 16
                                                            }
                                                        }}
                                                    >
                                                        <Avatar
                                                            src={conv.senderAvatar}
                                                            sx={{
                                                                bgcolor: "primary.main",
                                                                border: "2px solid",
                                                                borderColor: isUnread ? "primary.main" : "divider"
                                                            }}
                                                        >
                                                            {conv.sender?.tenantNameEn?.charAt(0)}
                                                        </Avatar>
                                                    </Badge>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center"
                                                        }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: isUnread ? 700 : 400,
                                                                    flex: 1,
                                                                    pr: 1,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap"
                                                                }}
                                                            >
                                                                {conv.sender?.tenantNameEn}
                                                            </Typography>
                                                            {conv.lastMessage?.createdAt && (
                                                                <Typography variant="caption" color="text.secondary"
                                                                            sx={{flexShrink: 0, fontSize: "0.65rem"}}>
                                                                    {formatDate(conv.lastMessage.createdAt)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant="body2" color="text.secondary"
                                                            sx={{
                                                                display: "-webkit-box", WebkitLineClamp: 1,
                                                                WebkitBoxOrient: "vertical", overflow: "hidden",
                                                                fontWeight: isUnread ? 600 : 400, fontSize: "0.8rem"
                                                            }}
                                                        >
                                                            {conv.lastMessage?.senderId === tenantId ? `${t("You") || "You"}: ` : ""}
                                                            {conv.lastMessage?.text}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            {idx < filteredConversations.length - 1 && (
                                                <Divider component="li" sx={{mx: 2}}/>
                                            )}
                                        </React.Fragment>
                                    )
                                })}

                                {hasMore && (
                                    <Box sx={{p: 2, textAlign: "center"}}>
                                        <Button
                                            variant="text" size="small"
                                            onClick={() => fetchConversations(page + 1, true)}
                                            disabled={loadingMore}
                                            startIcon={loadingMore ? <CircularProgress size={14}/> : <MoreHorizIcon/>}
                                            sx={{textTransform: "none"}}
                                        >
                                            {loadingMore ? t("Loading") : t("LoadMore")}
                                        </Button>
                                    </Box>
                                )}
                            </List>
                        )}
                    </Box>
                </Paper>

                {/* ── Right panel: active chat ── */}
                {selectedConversation ? (
                    <Paper elevation={2} sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden"
                    }}>

                        {/* Chat header */}
                        <Box sx={{
                            p: 2,
                            background: "linear-gradient(to right, #f5f7fa, #e4e8f0)",
                            borderBottom: 1, borderColor: "divider", flexShrink: 0
                        }}>
                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setSelectedConversation(null)}
                                        sx={{display: {xs: "flex", md: "none"}}}
                                    >
                                        <ArrowBackIcon/>
                                    </IconButton>
                                    <Avatar src={selectedConversation.senderAvatar} sx={{width: 42, height: 42}}>
                                        {selectedConversation.sender?.tenantNameEn?.charAt(0)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={600}>
                                            {selectedConversation.sender?.tenantNameEn}
                                        </Typography>
                                        {selectedConversation.sender?.address && (
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{
                                                            display: "block",
                                                            maxWidth: 280,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}
                                            >
                                                {addressToString(selectedConversation.sender.address)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <IconButton size="small" onClick={() => setSelectedConversation(null)}>
                                    <CloseIcon/>
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Messages area */}
                        <Box
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            sx={{
                                flex: 1, overflowY: "auto", p: 2, bgcolor: "#f8f9fa",
                                "&::-webkit-scrollbar": {width: "6px"},
                                "&::-webkit-scrollbar-track": {background: "transparent"},
                                "&::-webkit-scrollbar-thumb": {background: "rgba(0,0,0,0.2)", borderRadius: "3px"},
                            }}
                        >
                            {loadingMessages && messagePage > 0 && (
                                <Typography align="center" variant="caption" color="text.secondary"
                                            sx={{display: "block", mb: 1}}>
                                    {t("LoadingOlderMessages")}
                                </Typography>
                            )}

                            {messages.length === 0 && !loadingMessages && (
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%"
                                }}>
                                    <MailIcon sx={{fontSize: 56, color: "grey.300", mb: 1}}/>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("NoMessages")}
                                    </Typography>
                                </Box>
                            )}

                            {messages.map((msg, idx) => (
                                <Box key={msg.messageId || idx}>
                                    {(idx === 0 || isDifferentDay(messages[idx - 1].createdAt, msg.createdAt)) && (
                                        <Box sx={{display: "flex", alignItems: "center", my: 2}}>
                                            <Divider sx={{flex: 1}}/>
                                            <Typography variant="caption" sx={{
                                                mx: 2, px: 2, py: 0.5,
                                                bgcolor: "white", borderRadius: 2,
                                                color: "text.secondary", fontWeight: 500,
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                                            }}>
                                                {formatDateLabel(msg.createdAt)}
                                            </Typography>
                                            <Divider sx={{flex: 1}}/>
                                        </Box>
                                    )}
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: msg.senderId === tenantId ? "flex-end" : "flex-start",
                                        mb: 1.5
                                    }}>
                                        <Paper sx={{
                                            p: 1.5, maxWidth: "65%",
                                            bgcolor: msg.senderId === tenantId ? "primary.main" : "white",
                                            color: msg.senderId === tenantId ? "white" : "text.primary",
                                            borderRadius: msg.senderId === tenantId
                                                ? "18px 18px 4px 18px"
                                                : "18px 18px 18px 4px",
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                                        }}>
                                            <Typography variant="body2" sx={{
                                                whiteSpace: "pre-wrap", wordBreak: "break-word", mb: 0.5
                                            }}>
                                                {msg.text}
                                            </Typography>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                justifyContent: "flex-end"
                                            }}>
                                                <Typography variant="caption" sx={{opacity: 0.7, fontSize: "0.68rem"}}>
                                                    {formatTime(msg.createdAt)}
                                                </Typography>
                                                {msg.senderId === tenantId && (
                                                    msg.isRead
                                                        ? <DoneAllIcon sx={{fontSize: "0.85rem", opacity: 0.7}}/>
                                                        : <DoneIcon sx={{fontSize: "0.85rem", opacity: 0.7}}/>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Box>
                            ))}
                            <div ref={messagesEndRef}/>
                        </Box>

                        {/* Message input */}
                        <Box sx={{p: 2, borderTop: 1, borderColor: "divider", bgcolor: "white", flexShrink: 0}}>
                            <TextField
                                fullWidth multiline maxRows={4}
                                value={newText}
                                onChange={e => setNewText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={t("TypeYourMessage")}
                                variant="outlined"
                                sx={{"& .MuiOutlinedInput-root": {borderRadius: 3, bgcolor: "#f8f9fa"}}}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton size="small"><AttachFileIcon/></IconButton>
                                            <IconButton size="small"><EmojiIcon/></IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton color="primary" onClick={sendMessage}
                                                        disabled={!newText.trim()}>
                                                <SendIcon/>
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    </Paper>
                ) : (
                    /* Empty state (desktop only) */
                    <Paper elevation={1} sx={{
                        flex: 1,
                        display: {xs: "none", md: "flex"},
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 3,
                        bgcolor: "#fafafa"
                    }}>
                        <MailIcon sx={{fontSize: 72, color: "grey.300", mb: 2}}/>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {t("SelectConversation") || "Select a conversation"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={280}>
                            {t("SelectConversationDesc") || "Choose a conversation from the left to start chatting"}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </PageContainer>
    )
}

export default AllMessages
